import * as breakpoint from '@pixelunion/breakpoint';
import EventHandler from '@pixelunion/events';
import Flickity from 'flickity';
import 'flickity-fade';
import ImageZoom from './ImageZoom';
import loadYoutubeIframeAPI from '../utils/youtubeIframeAPIHelper';

export default class ProductGallery {
  constructor(el, settings) {
    this.el = el;
    this.settings = settings;
    this.viewport = this.el.querySelector('[data-product-gallery-viewport]');
    this.navigation = this.el.querySelector('[data-product-gallery-navigation]');
    this.figures = this.viewport.querySelectorAll('[data-product-gallery-figure]');
    this.media = [];
    this.selected = {
      figure: this.viewport.querySelector('[data-product-gallery-selected="true"]'),
      thumbnail: this.navigation ? this.navigation.querySelector('[data-product-gallery-selected="true"]') : null,
    };
    this.imageZoom = null;
    this.events = new EventHandler();

    if (this.navigation) {
      this._initFlickity();

      this.thumbnails = this.navigation.querySelectorAll('[data-product-gallery-thumbnail]');
      this.navigationArrows = this.navigation.querySelectorAll('.product-slideshow-navigation');

      this.onNavigationArrowClick = e => {
        const direction = e.target.dataset.slideshowMovement;
        if (direction === 'next') {
          this.flickity.next(true);
        } else {
          this.flickity.previous(true);
        }
      };

      this.events.register(this.navigation, 'click', event => {
        if ('productGalleryThumbnail' in event.target.dataset) {
          this.flickity.select(parseInt(event.target.dataset.productGalleryThumbnail, 10));
        }
      });

      this.events.register(this.navigation, 'focusin', event => {
        // Selects thumbnails when tabbing, bringing focused thumbnail into view
        // Note that thumbnails can be selected in the flickity slider
        // without the corresponding media being selected in the main viewport.
        // Main viewport is only updated when thumbnail is selected with a
        // click event, which includes activation with the keyboard.
        if (!('productGalleryThumbnail' in event.target.dataset)) return;
        this.flickityThumbnails.select(parseInt(event.target.dataset.productGalleryThumbnail, 10));
      });

      this.navigationArrows.forEach(arrow => this.events.register(arrow, 'click', this.onNavigationArrowClick));
      breakpoint.onChange(breakpoints => this._onBreakpointChange(breakpoints));
    }

    if (this.settings.enable_zoom) {
      this.figures.forEach(figure => {
        if (figure.dataset.mediaType !== 'image') return;
        const player = new ImageZoom({ el: figure });
        this.media.push({
          type: 'image',
          figure,
          player,
          focus: () => {},
          restart: () => {},
          pause: () => {},
          play: () => {},
          unload: () => player.unload(),
        });
      });
    }

    const features = [];

    if (this.el.querySelector('[data-media-type="model"]')) {
      this.events.register(this.viewport, 'click', e => {
        if ('shopifyXr' in e.target.dataset) {
          this._onViewInYourSpaceClick(e.target);
        }
      });

      features.push(
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: () => this._onModelLibraryLoad(),
        },
      );
      features.push(
        {
          name: 'shopify-xr',
          version: '1.0',
        },
      );
    }

    if (this.el.querySelector('[data-media-type="video"]')) {
      Promise.all([
        new Promise(resolve => {
          if (!document.querySelector('#plyr-stylesheet')) {
            const link = document.createElement('link');

            link.setAttribute('id', 'plyr-stylesheet');
            link.setAttribute('rel', 'stylesheet');
            link.setAttribute('href', 'https://cdn.shopify.com/shopifycloud/shopify-plyr/v1.0/shopify-plyr.css');
            link.onload = resolve;
            document.body.appendChild(link);
          } else {
            resolve();
          }
        }),
        new Promise(resolve => {
          features.push({
            name: 'video-ui',
            version: '1.0',
            onLoad: resolve,
          });
        }),
      ]).then(() => this._onVideoLibraryLoad());
    }

    loadYoutubeIframeAPI().then(() => this._onExternalVideoLibraryLoad());

    if (features.length) {
      Shopify.loadFeatures(features);
    }
  }

  selectMediaByVariant(variant) {
    if (!variant.featured_media) return;

    const figure = this.viewport.querySelector(`[data-media="${variant.featured_media.id}"]`);
    this._selectMediaByEl(figure);
  }

  unload() {
    this.events.unregisterAll();
    this.media.forEach(m => m.unload());
    this._disableFlickity();
  }

  _onModelLibraryLoad() {
    const onPlayListener = () => {
      if (this.flickity) {
        this.flickity.unbindDrag();
      }
    };
    const onPauseListener = () => {
      if (this.flickity) {
        this.flickity.bindDrag();
      }
    };
    const modelFigures = this.viewport.querySelectorAll('[data-media-type="model"]');
    const controls = ['zoom-in', 'zoom-out'];
    if (document.fullscreenEnabled) controls.push('fullscreen');

    modelFigures.forEach(figure => {
      const player = new Shopify.ModelViewerUI(figure.querySelector('model-viewer'), { controls });
      const modelEl = figure.querySelector('model-viewer');
      this.media.push({
        type: 'model',
        figure,
        player,
        focus: () => modelEl.focus(),
        restart: () => {},
        pause: () => player.pause(),
        play: () => player.play(),
        unload: () => {},
      });

      this.events.register(modelEl, 'shopify_model_viewer_ui_toggle_play', onPlayListener);
      this.events.register(modelEl, 'shopify_model_viewer_ui_toggle_pause', onPauseListener);
    });
  }

  _onViewInYourSpaceClick(target) {
    if (target.dataset.shopifyModel3dId === this.selected.figure.dataset.media) return;

    const figure = this.viewport.querySelector(`[data-media="${target.dataset.shopifyModel3dId}"]`);
    this._selectMediaByEl(figure);
  }

  _onBreakpointChange({ previous, current }) {
    if (document.fullscreenElement) {
      return;
    }

    if (previous.value === 'S' || current.value === 'S') {
      // Desktop and mobile use slightly different config,
      // so we need to re-initialize when switching between them.
      this._disableFlickity();
      this._initFlickity();
    }
  }

  _selectMediaByEl(el) {
    if (this.flickity) {
      this.flickity.select(parseInt(el.dataset.productGalleryFigure, 10));
    }
  }

  _onVideoLibraryLoad() {
    const videoFigures = this.viewport.querySelectorAll('[data-media-type="video"]');

    videoFigures.forEach(videoFigure => {
      const player = new Shopify.Plyr(
        videoFigure.querySelector('video'),
        {
          loop: { active: this.settings.enable_video_looping },
        },
      );
      const plyrEl = videoFigure.querySelector('.plyr');
      const video = {
        type: 'video',
        figure: videoFigure,
        player,
        focus: () => plyrEl.focus(),
        restart: () => {
          player.restart();
          player.play();
        },
        pause: () => player.pause(),
        play: () => player.play(),
        unload: () => player.destroy(),
      };

      this.events.register(videoFigure, 'play', () => this._pauseMedia(video));
      this.media.push(video);
    });
  }

  _onExternalVideoLibraryLoad() {
    const videoFigures = this.viewport.querySelectorAll('[data-media-type="external_video"]');

    videoFigures.forEach(videoFigure => {
      let playerReady = false;
      const player = new YT.Player(videoFigure.querySelector('iframe'), {
        events: {
          onReady: () => { playerReady = true; },
        },
      });
      const video = {
        type: 'video',
        figure: videoFigure,
        player,
        focus: () => player.getIframe().focus(),
        restart: () => {
          if (!playerReady) return;
          player.seekTo(0);
          player.playVideo();
        },
        pause: () => playerReady && player.pauseVideo(),
        play: () => playerReady && player.playVideo(),
        unload: () => {},
      };

      player.addEventListener('onStateChange', ({ data }) => {
        if (data === YT.PlayerState.ENDED && this.settings.enable_video_looping) {
          video.restart();
        } else if (data === YT.PlayerState.PLAYING) {
          this._pauseMedia(video);
        }
      });

      this.media.push(video);
    });
  }

  _onMediaSelect(media) {
    if (!media) return;
    switch (media.type) {
      case 'video':
        if (this.settings.enable_video_autoplay && breakpoint.min('L')) {
          media.play();
        }
        break;
      case 'model':
        if (document.querySelector('html').classList.contains('no-touch') && breakpoint.min('L')) {
          media.play();
        }
        break;
      default:
        break;
    }
  }

  _pauseMedia(excludeMedia = null) {
    this.media.forEach(m => {
      if (m !== excludeMedia) {
        m.pause();
      }
    });
  }

  _selectMediaByIndex(index) {
    const figure = this.figures[index];

    this.selected.figure.dataset.productGallerySelected = false;
    this.selected.figure.setAttribute('aria-hidden', true);

    this.selected.figure = figure;
    this.selected.figure.dataset.productGallerySelected = true;
    this.selected.figure.setAttribute('aria-hidden', false);


    if (this.navigation) {
      const thumbnail = this.thumbnails[index];

      this.selected.thumbnail.dataset.productGallerySelected = false;
      this.selected.thumbnail = thumbnail;
      this.selected.thumbnail.dataset.productGallerySelected = true;
    }
    this._pauseMedia();
    this._onMediaSelect(this.media.filter(m => m.figure === this.selected.figure)[0]);
  }

  _initFlickity() {
    const flickityGalleryOptions = {
      accessibility: false,
      draggable: true,
      wrapAround: true,
      freeScroll: false,
      initialIndex: parseInt(this.selected.figure.dataset.productGalleryFigure, 10),
      cellSelector: '[data-product-gallery-figure]',
      fade: breakpoint.max('S'),
      friction: 0.5,
      selectedAttraction: 0.1,
      adaptiveHeight: true,
      pageDots: false,
      prevNextButtons: false,
    };

    const flickityThumbnailOptions = {
      accessibility: false,
      pageDots: false,
      wrapAround: this._shouldThumbnailsWrap(),
      initialIndex: parseInt(this.selected.figure.dataset.productGalleryFigure, 10),
      prevNextButtons: false,
      cellSelector: '[data-product-gallery-thumbnail]',
    };

    this.flickity = new Flickity(this.viewport, flickityGalleryOptions);
    this.flickityThumbnails = new Flickity(this.navigation, flickityThumbnailOptions);
    this.navScroller = this.flickityThumbnails.element;

    this.flickityEvent = this.events.register(this.viewport, 'rimg:load', () => this.flickity.resize());
    this.flickityThumbnailsEvent = this.events.register(this.navigation, 'rimg:load', () => this.flickityThumbnails.resize());
    this.viewport.querySelectorAll('video').forEach(video => this.events.register(video, 'loadeddata', () => {
      if (this.flickity) {
        this.flickity.resize();
      }
    }));

    // Ensures models and video auto-play when swiping through the slideshow
    this.flickity.on('change', index => {
      this._selectMediaByIndex(index);
      if (this.flickityThumbnails.selectedIndex !== index) {
        this.flickityThumbnails.select(index);
      }
    });

    this.flickity.on('settle', () => {
      this.media.filter(m => m.figure === this.selected.figure)[0].focus();
    });

    // Flickity controls the pointerdown event which prevents model-viewer-ui from
    // triggering .play() on mouseup. This returns this functionality.
    this.flickity.on('staticClick', (event, pointer, cellElement) => {
      // Ignore anything but mouse events, touch events currently are bugged due to model-viewer-ui
      // touchEnd triggers the model to pause after we play here, returning the model to a paused state
      if (event.pointerType !== 'mouse') return;
      if (event.target.className === 'product-gallery--viewinyourspace' || !cellElement) {
        return;
      }

      const figure = cellElement.dataset.mediaType === 'model' ? cellElement : false;
      if (figure) {
        const model = this.media.filter(item => item.figure === figure)[0];
        model.play();
      }
    });
  }

  _shouldThumbnailsWrap() {
    // Mobile = 2 arrows + 3 slides
    // Desktop = 2 arrows + 5 slides
    return breakpoint.max('S') ? this.navigation.childElementCount > 5 : this.navigation.childElementCount > 7;
  }

  _disableFlickity() {
    if (this.flickity) {
      this.flickity.destroy();
      this.flickity = null;
      this.events.unregister(this.flickityEvent);
    }

    if (this.flickityThumbnails) {
      this.flickityThumbnails.destroy();
      this.flickityThumbnails = null;
      this.events.unregister(this.flickityThumbnailsEvent);
    }
  }
}
